/**
 * Create/edit modal for workspace calendar events (PRD §5.2).
 * Workspace events are internal-only: visible to members of this workspace,
 * never on any public surface. Author or workspace owner can edit/delete —
 * enforced server-side; the modal surfaces the server's message on 403.
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../../../lib/api';
import AddressAutocomplete from '../../../AddressAutocomplete';
import Button from '@shared/ui/Button';
import { DeleteIcon } from '../../../icons';
import { HelpTrigger } from '../../../../help';
import type { CalendarEvent } from '../../../../types/calendar';
import TimeComboBox, { addMinutes } from '../../../calendar/TimeComboBox';
import ConfirmationDialog from '../../../ConfirmationDialog';

interface WorkspaceEventModalProps {
  workspaceId: string;
  /** Event being edited; null = create mode */
  event: CalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_FORM = {
  title: '',
  event_date: '',
  start_time: '',
  end_time: '',
  format: 'in_person',
  venue: '',
  address: '',
  city: '',
  description: '',
  link_url: ''
};

const inputClasses =
  'w-full rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-tertiary px-3 py-1.5 text-sm text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClasses = 'block text-xs font-semibold text-slate-600 dark:text-text-dark-secondary mb-1';

const WorkspaceEventModal: React.FC<WorkspaceEventModalProps> = ({
  workspaceId,
  event,
  isOpen,
  onClose,
  onSaved
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOpen) return;
    if (event) {
      setForm({
        title: event.title || event.reading_title || '',
        event_date: (event.event_date || event.reading_date || '').slice(0, 10),
        start_time: event.start_time ? String(event.start_time).slice(0, 5) : '',
        end_time: event.end_time ? String(event.end_time).slice(0, 5) : '',
        format: event.format && event.format !== 'unknown' ? event.format : 'in_person',
        venue: event.venue || '',
        address: event.address || '',
        city: event.city || '',
        description: event.calendar_description || '',
        link_url: (event as CalendarEvent & { link_url?: string }).link_url || ''
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [isOpen, event]);

  if (!isOpen) return null;

  const set = (field: keyof typeof EMPTY_FORM) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((current) => ({ ...current, [field]: e.target.value }));

  // Outlook-style defaults: picking a start assumes an hour-long event; moving
  // the start keeps the current duration.
  const setStartTime = (newStart: string) => {
    setForm((current) => {
      if (!newStart) return { ...current, start_time: '' };
      let end = current.end_time;
      if (current.start_time && end) {
        const duration =
          (parseInt(end.slice(0, 2), 10) * 60 + parseInt(end.slice(3, 5), 10)) -
          (parseInt(current.start_time.slice(0, 2), 10) * 60 + parseInt(current.start_time.slice(3, 5), 10));
        end = addMinutes(newStart, duration > 0 ? duration : 60);
      } else {
        end = addMinutes(newStart, 60);
      }
      return { ...current, start_time: newStart, end_time: end };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (event?.authored_event_id) {
        await api.put(`/workspaces/${workspaceId}/events/${event.authored_event_id}`, form);
        toast.success('Event updated');
      } else {
        await api.post(`/workspaces/${workspaceId}/events`, form);
        toast.success('Event added');
      }
      // The Pro home calendar lists workspace events too.
      queryClient.invalidateQueries(['proHome', 'calendar']);
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!event?.authored_event_id) return;
    setConfirmingDelete(false);
    setDeleting(true);
    try {
      await api.delete(`/workspaces/${workspaceId}/events/${event.authored_event_id}`);
      toast.success('Event deleted');
      queryClient.invalidateQueries(['proHome', 'calendar']);
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-background-dark-secondary shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-border-light dark:border-border-dark-hairline px-5 py-3.5">
          <h2 className="flex items-center gap-2 font-display font-semibold text-[15px] text-primary-800 dark:text-text-dark-primary">
            {event ? 'Edit Event' : 'Add Event'}
            <HelpTrigger contentKey="workspace-events" />
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label htmlFor="we-title" className={labelClasses}>Title *</label>
            <input id="we-title" className={inputClasses} value={form.title} onChange={set('title')} maxLength={200} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="we-date" className={labelClasses}>Date *</label>
              <input id="we-date" type="date" className={inputClasses} value={form.event_date} onChange={set('event_date')} />
            </div>
            <div>
              <label htmlFor="we-start" className={labelClasses}>Start</label>
              <TimeComboBox id="we-start" value={form.start_time} onChange={setStartTime} />
            </div>
            <div>
              <label htmlFor="we-end" className={labelClasses}>End</label>
              <TimeComboBox
                id="we-end"
                value={form.end_time}
                baseTime={form.start_time || undefined}
                onChange={(v) => setForm((current) => ({ ...current, end_time: v }))}
              />
            </div>
          </div>
          <div>
            <label htmlFor="we-format" className={labelClasses}>Format</label>
            <select id="we-format" className={inputClasses} value={form.format} onChange={set('format')}>
              <option value="in_person">In person</option>
              <option value="virtual">Virtual</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          {form.format !== 'virtual' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="we-venue" className={labelClasses}>Venue</label>
                <input id="we-venue" className={inputClasses} value={form.venue} onChange={set('venue')} maxLength={255} />
              </div>
              <div>
                <label htmlFor="we-city" className={labelClasses}>City</label>
                <input id="we-city" className={inputClasses} value={form.city} onChange={set('city')} maxLength={100} />
              </div>
              <div className="col-span-2">
                <label htmlFor="we-address" className={labelClasses}>Address</label>
                {/* Google Places suggestions; picking one fills City too. */}
                <AddressAutocomplete
                  id="we-address"
                  inputClassName={inputClasses}
                  showIcon={false}
                  placeholder=""
                  value={form.address}
                  onChange={(value) => setForm((current) => ({ ...current, address: value.slice(0, 255) }))}
                  onSelect={(parts) => setForm((current) => ({ ...current, city: parts.city || current.city }))}
                />
              </div>
            </div>
          )}
          <div>
            <label htmlFor="we-link" className={labelClasses}>Link (agenda, Zoom, RSVP)</label>
            <input id="we-link" type="url" className={inputClasses} value={form.link_url} onChange={set('link_url')} placeholder="https://…" />
          </div>
          <div>
            <label htmlFor="we-desc" className={labelClasses}>Description</label>
            <textarea id="we-desc" rows={3} className={inputClasses} value={form.description} onChange={set('description')} maxLength={2000} />
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border-light dark:border-border-dark-hairline px-5 py-3">
          <div>
            {event?.authored_event_id && (
              <Button variant="danger" size="sm" icon={DeleteIcon} loading={deleting} onClick={() => setConfirmingDelete(true)}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={!form.title.trim() || !form.event_date}
              onClick={save}
            >
              {event ? 'Save Changes' : 'Add Event'}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={confirmingDelete}
        title="Delete event"
        message="Delete this event for everyone in the workspace?"
        confirmText="Delete"
        variant="danger"
        onConfirm={remove}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
};

export default WorkspaceEventModal;
